// ------------------------------------------------------------------------------
// <copyright file="ScenarioTesting.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

/**
 * ScenarioTesting — E2E scenario testing page for AdminPortal.
 *
 * Allows SuperAdmins to run predefined billing/plan state scenarios against
 * a selected environment. Creates test tenants in exact billing states so
 * QA can log in as those users and verify UI behavior manually.
 *
 * Layout mirrors ApiTestHarness: sticky top bar + sidebar + main panel.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  FlaskConical, AlertTriangle, Search, Play, Copy, Check,
  Save, X, Key, ChevronRight, ChevronDown, Trash2,
  Gift, Zap, Lock, Activity, Star, Award, Briefcase,
  Globe, Video, Ticket, ArrowUpDown, Monitor,
  Shield, CreditCard, Users, RefreshCw, CheckCircle,
  Tag, ExternalLink,
  UserX, KeyRound, Building2, Layers, Smartphone,
} from 'lucide-react';
import { ENVIRONMENTS } from '../config/apiHarnessConfig';
import { SCENARIO_CATEGORIES } from '../config/scenarioTestingConfig';
import useScenarioRunner from '../hooks/useScenarioRunner';
import { saveCredentials } from '../utils/scenarioStorage';
import { trackEvent, trackException } from '../services/appInsightsService';
import './ScenarioTesting.css';

// ---------------------------------------------------------------------------
// Icon map — resolves icon name string from category config
// ---------------------------------------------------------------------------
const ICON_MAP = {
  Lock, Gift, Zap, Activity, Star, Award, Briefcase,
  Globe, Video, Ticket, ArrowUpDown, Monitor,
  Shield, CreditCard, Trash2, Users, RefreshCw,
  CheckCircle, Tag, ExternalLink,
  UserX, KeyRound, Building2, Layers, AlertTriangle, Smartphone,
};

/**
 * Render a category icon by name string.
 *
 * @param {object} props
 * @param {string} props.name - lucide-react icon name
 * @param {number} [props.size]
 */
function CategoryIcon({ name, size = 14 }) {
  const Icon = ICON_MAP[name] || Activity;
  return <Icon size={size} />;
}

// ---------------------------------------------------------------------------
// JSON syntax highlighter (matches ApiTestHarness pattern)
// ---------------------------------------------------------------------------
/**
 * Syntax-highlight a JSON value for display in a <pre> element.
 *
 * @param {any} json - Value to highlight (will be JSON.stringified if not string)
 * @returns {string} HTML string with span-wrapped tokens
 */
function highlightJson(json) {
  let str = json;
  if (typeof str !== 'string') {
    str = JSON.stringify(json, null, 2);
  }
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        let cls = 'json-number';
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? 'json-key' : 'json-string';
        } else if (/true|false/.test(match)) {
          cls = 'json-bool';
        } else if (/null/.test(match)) {
          cls = 'json-null';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
}

// ---------------------------------------------------------------------------
// CopyButton
// ---------------------------------------------------------------------------
/**
 * Button that copies text to clipboard and shows a brief confirmation.
 *
 * @param {object} props
 * @param {string} props.text - Text to copy
 * @param {string} [props.label]
 */
function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      trackException(err, { errorType: 'ScenarioTesting_CopyFailed' });
    });
  }, [text]);

  return (
    <button className="st-copy-btn" onClick={handleCopy} title="Copy to clipboard">
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {label && <span>{copied ? 'Copied!' : label}</span>}
    </button>
  );
}

// ---------------------------------------------------------------------------
// StepRow — single step in the timeline
// ---------------------------------------------------------------------------
/**
 * Renders a single scenario step with method badge, path, status dot, and
 * optionally the response JSON viewer.
 *
 * @param {object} props
 * @param {object} props.step - Step definition
 * @param {object} props.result - Step result { status, httpStatus, response, durationMs, error? }
 * @param {boolean} props.isCurrentStep - True if this step is actively running
 */
function StepRow({ step, result, isCurrentStep }) {
  const [expanded, setExpanded] = useState(false);

  const statusClass = isCurrentStep
    ? 'st-step-status-running'
    : result
      ? (result.status === 'pass' ? 'st-step-status-pass' : 'st-step-status-fail')
      : 'st-step-status-pending';

  const methodClass = `st-method st-method-${step.method.toLowerCase()}`;

  return (
    <div className="st-step">
      <div className="st-step-header">
        <div className={`st-step-status ${statusClass}`} />
        <span className={methodClass}>{step.method}</span>
        <span className="st-step-name">{step.name}</span>
        {result && (
          <span className="st-step-duration">{result.durationMs}ms</span>
        )}
        {result && result.response !== null && result.response !== undefined && (
          <button
            className="st-copy-btn"
            onClick={() => setExpanded(e => !e)}
            title={expanded ? 'Hide response' : 'Show response'}
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        )}
      </div>
      <div className="st-step-path">{step.path}</div>
      {result && result.error && (
        <div className="st-step-error">{result.error}</div>
      )}
      {expanded && result && result.response !== null && result.response !== undefined && (
        <pre
          className="st-step-response"
          dangerouslySetInnerHTML={{ __html: highlightJson(result.response) }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScenarioCard — collapsed + expanded views
// ---------------------------------------------------------------------------
/**
 * Renders a single scenario as a collapsible card.
 *
 * @param {object} props
 * @param {object} props.scenario - Scenario definition
 * @param {object} props.runner - useScenarioRunner instance
 * @param {string} props.environment - Selected environment URL
 * @param {string} props.functionKey - Function key value
 */
function ScenarioCard({ scenario, runner, environment, functionKey }) {
  const [expanded, setExpanded] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [savedToVault, setSavedToVault] = useState(false);

  const runnerResults = (runner && runner.results) || {};
  const scenarioResults = scenario.steps.reduce((acc, step) => {
    if (runnerResults[step.id]) acc[step.id] = runnerResults[step.id];
    return acc;
  }, {});

  const hasResults = Object.keys(scenarioResults).length > 0;
  const allPassed = hasResults && scenario.steps.every(s => scenarioResults[s.id]?.status === 'pass');

  const handleRun = useCallback(async () => {
    setExpanded(true);
    if (!runner.runScenario) return;
    try {
      await runner.runScenario(scenario, overrides);
    } catch (err) {
      trackException(err instanceof Error ? err : new Error(String(err)), {
        scenarioId: scenario.id,
        errorType: 'ScenarioCard_RunFailed',
      });
    }
  }, [runner, scenario, overrides]);

  const handleSaveToVault = useCallback(() => {
    const extracted = (runner && runner.extractedVars) || {};
    const resolvedEmail = overrides.email || extracted.email ||
      (typeof scenario.variables.email === 'function' ? scenario.variables.email() : scenario.variables.email) || '';
    const resolvedPassword = overrides.password || scenario.variables.password || '';

    saveCredentials({
      scenarioId: scenario.id,
      scenarioName: scenario.name,
      email: resolvedEmail,
      password: resolvedPassword,
      tenantId: extracted.tenantId || '',
      planTier: scenario.expectedState?.planTier || 'Unknown',
    });

    setSavedToVault(true);
    setTimeout(() => setSavedToVault(false), 2000);

    trackEvent('ScenarioTesting.SavedToVault', { scenarioId: scenario.id });
  }, [runner, scenario, overrides]);

  const copyCredentials = useCallback(() => {
    const extracted = (runner && runner.extractedVars) || {};
    const email = overrides.email || extracted.email ||
      (typeof scenario.variables.email === 'function' ? scenario.variables.email() : scenario.variables.email) || '';
    const password = overrides.password || scenario.variables.password || '';
    navigator.clipboard.writeText(`Email: ${email}\nPassword: ${password}`).catch((err) => {
      trackException(err instanceof Error ? err : new Error(String(err)), {
        scenarioId: scenario.id,
        errorType: 'ScenarioTesting_CopyCredentialsFailed',
      });
    });
  }, [runner, scenario, overrides]);

  return (
    <div className="st-scenario-card">
      <div
        className="st-scenario-card-header"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(e => !e)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(ex => !ex); } }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="st-scenario-name">{scenario.name}</span>
        <div className="st-scenario-tags">
          {scenario.tags.slice(0, 2).map(tag => (
            <span key={tag} className="st-tag">{tag}</span>
          ))}
        </div>
        <span className="st-step-count">{scenario.steps.length} steps</span>
        <button
          className="st-run-btn"
          onClick={(e) => { e.stopPropagation(); handleRun(); }}
          disabled={runner && runner.running}
          title="Run this scenario"
        >
          <Play size={11} />
          Run
        </button>
      </div>

      {expanded && (
        <div className="st-scenario-body">
          <p className="st-scenario-description">{scenario.description}</p>

          {/* Variable overrides */}
          {Object.keys(scenario.variables).length > 0 && (
            <div>
              {Object.entries(scenario.variables).map(([key, defaultVal]) => {
                const placeholder = typeof defaultVal === 'function' ? '(auto-generated)' : String(defaultVal);
                return (
                  <div key={key} style={{ marginBottom: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ fontSize: 12, color: 'var(--st-text-muted)', width: 80, flexShrink: 0 }}>{key}</label>
                    <input
                      style={{
                        flex: 1,
                        background: 'var(--st-input)',
                        border: '1px solid var(--st-border)',
                        borderRadius: 4,
                        color: 'var(--st-text)',
                        fontSize: 12,
                        fontFamily: 'var(--st-font-mono)',
                        padding: '0.25rem 0.5rem',
                        outline: 'none',
                      }}
                      placeholder={placeholder}
                      value={overrides[key] || ''}
                      onChange={(e) => setOverrides(prev => ({ ...prev, [key]: e.target.value }))}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Step timeline */}
          <div className="st-steps">
            {scenario.steps.map((step) => (
              <StepRow
                key={step.id}
                step={step}
                result={scenarioResults[step.id]}
                isCurrentStep={runner && runner.currentStep === step.id}
              />
            ))}
          </div>

          {/* Expected state */}
          {scenario.expectedState && Object.keys(scenario.expectedState).length > 0 && (
            <div className="st-expected-state">
              <div className="st-expected-state-title">Expected State (verify manually)</div>
              <div className="st-expected-state-body">
                {Object.entries(scenario.expectedState).map(([key, value]) => (
                  <div key={key} className="st-expected-item">
                    <span className="st-expected-key">{key}:</span>
                    <span className="st-expected-value">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Credentials actions — only shown after a successful run */}
          {allPassed && (
            <div className="st-cred-actions">
              <span style={{ fontSize: 12, color: 'var(--st-text-muted)' }}>Credentials:</span>
              <CopyButton text={
                // Use extracted email (set during run) or override; never re-invoke the
                // function-typed variable here — it would generate a new timestamp that
                // was never registered.
                (overrides.email || ((runner && runner.extractedVars && runner.extractedVars.email)) ||
                  (typeof scenario.variables.email !== 'function' ? scenario.variables.email : '') || '') +
                '\n' +
                (overrides.password || scenario.variables.password || '')
              } label="Copy" />
              <button className="st-save-btn" onClick={handleSaveToVault} title="Save to credentials vault">
                {savedToVault ? <Check size={12} /> : <Save size={12} />}
                <span>{savedToVault ? 'Saved!' : 'Save to Vault'}</span>
              </button>
              <button className="st-copy-btn" onClick={copyCredentials} title="Copy credentials">
                <Copy size={12} />
                <span>Copy creds</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScenarioTesting — main page component
// ---------------------------------------------------------------------------
/**
 * Main scenario testing page.
 *
 * Renders a sticky top bar (environment selector + function key input),
 * a sidebar (category list + search), and a main panel (scenario cards).
 */
function ScenarioTesting() {
  const [selectedEnvIndex, setSelectedEnvIndex] = useState(1); // Default: Staging
  const [functionKey, setFunctionKey] = useState(
    process.env.REACT_APP_DEFAULT_FUNCTION_KEY || sessionStorage.getItem('st-function-key') || ''
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const searchRef = useRef(null);

  const selectedEnv = ENVIRONMENTS[selectedEnvIndex] || ENVIRONMENTS[1];

  const runner = useScenarioRunner(selectedEnv.url, functionKey);

  const handleFunctionKeyChange = useCallback((e) => {
    const val = e.target.value;
    setFunctionKey(val);
    sessionStorage.setItem('st-function-key', val);
  }, []);

  const handleEnvSelect = useCallback((index) => {
    setSelectedEnvIndex(index);
    trackEvent('ScenarioTesting.EnvironmentSelected', { environment: ENVIRONMENTS[index]?.id });
  }, []);

  const handleCategorySelect = useCallback((categoryId) => {
    setSelectedCategoryId(categoryId);
    setSearchTerm('');
    trackEvent('ScenarioTesting.CategorySelected', { categoryId });
  }, []);

  // Filter categories by search term (name, description, or scenario names/tags)
  const filteredCategories = SCENARIO_CATEGORIES.filter((cat) => {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    if (cat.name.toLowerCase().includes(lower)) return true;
    if (cat.description.toLowerCase().includes(lower)) return true;
    return cat.scenarios.some(
      s => s.name.toLowerCase().includes(lower) ||
           s.description.toLowerCase().includes(lower) ||
           s.tags.some(t => t.toLowerCase().includes(lower))
    );
  });

  const selectedCategory = SCENARIO_CATEGORIES.find(c => c.id === selectedCategoryId);

  return (
    <div className="scenario-testing">
      {/* ----------------------------------------------------------------- */}
      {/* Top bar                                                           */}
      {/* ----------------------------------------------------------------- */}
      <div className="st-topbar">
        <div className="st-title">
          <FlaskConical size={16} />
          Scenario Testing
        </div>

        {/* Environment selector */}
        <div className="st-env-pills">
          {ENVIRONMENTS.map((env, idx) => (
            <button
              key={env.id}
              className={[
                'st-env-pill',
                env.dangerous ? 'st-env-pill-danger' : '',
                idx === selectedEnvIndex ? 'st-env-pill-active' : '',
              ].join(' ')}
              onClick={() => handleEnvSelect(idx)}
              title={env.url}
            >
              {env.label}
            </button>
          ))}
        </div>

        {/* Production warning */}
        {selectedEnv.dangerous && (
          <div className="st-prod-warning">
            <AlertTriangle size={12} />
            Production — test data creates real records
          </div>
        )}

        {/* Function key input */}
        <div className="st-function-key-wrap">
          <Key size={14} className="st-function-key-icon" />
          <input
            className="st-function-key-input"
            type="password"
            placeholder="Function Key"
            value={functionKey}
            onChange={handleFunctionKeyChange}
            title="Azure Functions host key (x-functions-key)"
          />
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Layout: sidebar + main                                            */}
      {/* ----------------------------------------------------------------- */}
      <div className="st-layout">
        {/* Sidebar */}
        <nav className="st-sidebar">
          {/* Search */}
          <div className="st-search-wrap">
            <Search size={13} className="st-search-icon" />
            <input
              ref={searchRef}
              className="st-search"
              type="text"
              placeholder="Search scenarios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="st-search-clear"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Category list */}
          <div className="st-sidebar-nav">
            {SCENARIO_CATEGORIES.map((category) => {
              const isVisible = filteredCategories.some(c => c.id === category.id);
              return (
                <button
                  key={category.id}
                  className={[
                    'st-sidebar-item',
                    category.id === selectedCategoryId ? 'st-sidebar-item-active' : '',
                    !isVisible ? 'st-sidebar-item-hidden' : '',
                  ].join(' ')}
                  onClick={() => handleCategorySelect(category.id)}
                  title={category.description}
                >
                  <CategoryIcon name={category.icon} size={14} />
                  <span className="st-sidebar-item-name">{category.name}</span>
                  <span className="st-category-badge">{category.scenarios.length}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main panel */}
        <main className="st-main">
          {!selectedCategory ? (
            <div className="st-empty-state">
              <FlaskConical size={40} />
              <div className="st-empty-title">Select a category</div>
              <div className="st-empty-sub">
                Choose a scenario category from the sidebar to view and run test scenarios.
              </div>
            </div>
          ) : (
            <>
              <div className="st-category-header">
                <CategoryIcon name={selectedCategory.icon} size={20} />
                <div>
                  <div className="st-category-title">{selectedCategory.name}</div>
                  <div className="st-category-description">{selectedCategory.description}</div>
                </div>
              </div>

              {selectedCategory.scenarios.map((scenario) => (
                <ScenarioCard
                  key={scenario.id}
                  scenario={scenario}
                  runner={runner}
                  environment={selectedEnv.url}
                  functionKey={functionKey}
                />
              ))}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export { ScenarioTesting };
export default ScenarioTesting;
